/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  Download,
  Image,
  Palette,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import useInstallPrompt from '../hooks/useInstallPrompt';
import { getUserProfile, updateUserProfile } from '../api/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';

const SETTINGS_SECTIONS = [
  {
    title: 'Settings Areas',
    accent: 'from-primary/15 via-primary/5 to-transparent',
    items: [
      {
        label: 'Theme',
        path: '/platform-admin',
        description: 'Shared appearance and presets.',
        icon: Palette,
      },
      {
        label: 'Notifications',
        path: '/settings/notifications',
        description: 'Push delivery and categories.',
        icon: Bell,
      },
      {
        label: 'Chart Snapshots',
        path: '/settings/chart-snapshot-configs',
        description: 'Capture configurations and presets.',
        icon: Image,
      },
    ],
  },
];

function SettingsSection({ section, onNavigate }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className={`bg-gradient-to-br ${section.accent} p-5 border-b border-borderDefault`}>
          <h2 className="text-lg font-semibold text-textPrimary">{section.title}</h2>
          {section.description ? <p className="mt-1 text-sm text-textSecondary">{section.description}</p> : null}
        </div>

        <div className="p-4 space-y-3">
          {section.items.map((item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.path}
                onClick={() => onNavigate(item.path)}
                className="group w-full rounded-xl border border-borderDefault bg-backgroundSurface p-4 text-left transition hover:border-primary/40 hover:bg-primary/5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg border border-borderDefault bg-backgroundInput p-2">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-textPrimary group-hover:text-primary transition-colors">
                        {item.label}
                      </p>
                      <p className="mt-1 text-sm text-textSecondary">{item.description}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-textSecondary group-hover:text-primary transition-colors mt-0.5" />
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const { canInstall, isInstalled, install } = useInstallPrompt();
  const [retrying, setRetrying] = useState(false);
  const [requireApproval, setRequireApproval] = useState(true);
  const [approvalLoading, setApprovalLoading] = useState(true);

  // Load trade approval preference from profile
  useEffect(() => {
    getUserProfile()
      .then((data) => {
        setRequireApproval(!!data?.require_trade_approval);
      })
      .catch(() => {})
      .finally(() => setApprovalLoading(false));
  }, []);

  const toggleApproval = async () => {
    const newVal = !requireApproval;
    setRequireApproval(newVal);
    try {
      await updateUserProfile({ require_trade_approval: newVal });
      toast.success(newVal ? 'Trade approval enabled' : 'Trade approval disabled');
    } catch (e) {
      setRequireApproval(!newVal); // revert
      toast.error('Failed to update setting');
    }
  };

  // Detect browser for manual instructions
  const isChrome = /chrome/i.test(navigator.userAgent) && !/edg/i.test(navigator.userAgent);
  const isEdge = /edg/i.test(navigator.userAgent);
  const isSafari = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent);
  const isMobile = /android|iphone|ipad/i.test(navigator.userAgent);

  const handleInstall = async () => {
    if (canInstall) {
      await install();
    }
  };

  /** Re-register SW and reload to re-trigger install eligibility */
  const handleRetryInstall = async () => {
    setRetrying(true);
    try {
      // Clear any install-dismissed localStorage flag (from banner)
      localStorage.removeItem('pwa-install-dismissed');

      // Force SW update to re-trigger install eligibility
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      }
      // Small delay, then hard reload
      await new Promise((r) => setTimeout(r, 300));
      window.location.reload();
    } catch {
      setRetrying(false);
    }
  };

  return (
    <div className="my-2 sm:my-4 md:my-6 mx-auto max-w-7xl space-y-4">
      <div className="px-1 sm:px-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-textPrimary">Settings</h1>
        <p className="mt-2 max-w-3xl text-sm sm:text-base text-textSecondary leading-relaxed">
          Device, approval, and workspace preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-4">
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent p-5 border-b border-borderDefault">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-backgroundSurface/90 p-2 border border-borderDefault">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-textPrimary">General</h2>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-5 space-y-4">
              <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg border border-borderDefault bg-backgroundInput p-2">
                    <Download className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-textPrimary">Install App</h3>

                    <div className="mt-3">
                      {isInstalled ? (
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20 text-green-400">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                          <p className="text-sm text-textSecondary">paxia.ai is installed on this device.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {canInstall ? (
                            <button
                              onClick={handleInstall}
                              className="w-full rounded-lg bg-blue-600 px-5 py-3 text-sm font-medium text-white hover:bg-blue-500 active:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Install paxia.ai
                            </button>
                          ) : (
                            <>
                              <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-4 space-y-3">
                                {isEdge ? (
                                  <>
                                    <div className="flex items-start gap-3">
                                      <span className="shrink-0 mt-0.5 text-blue-400 font-bold text-sm">1.</span>
                                      <p className="text-sm text-blue-200">
                                        Look for the <strong>install icon</strong> in Edge's address bar (looks like a monitor with a ↓ arrow)
                                      </p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                      <span className="shrink-0 mt-0.5 text-blue-400 font-bold text-sm">2.</span>
                                      <p className="text-sm text-blue-200">
                                        Or click <strong>⋯</strong> (menu) → <strong>Apps</strong> → <strong>Install this site as an app</strong>
                                      </p>
                                    </div>
                                  </>
                                ) : isChrome ? (
                                  <>
                                    <div className="flex items-start gap-3">
                                      <span className="shrink-0 mt-0.5 text-blue-400 font-bold text-sm">1.</span>
                                      <p className="text-sm text-blue-200">
                                        Look for the <strong>install icon</strong> (⊕) in Chrome's address bar
                                      </p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                      <span className="shrink-0 mt-0.5 text-blue-400 font-bold text-sm">2.</span>
                                      <p className="text-sm text-blue-200">
                                        Or click <strong>⋮</strong> (menu) → <strong>Install paxia.ai…</strong>
                                      </p>
                                    </div>
                                  </>
                                ) : isSafari && isMobile ? (
                                  <>
                                    <div className="flex items-start gap-3">
                                      <span className="shrink-0 mt-0.5 text-blue-400 font-bold text-sm">1.</span>
                                      <p className="text-sm text-blue-200">
                                        Tap the <strong>Share</strong> button (rectangle with ↑ arrow)
                                      </p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                      <span className="shrink-0 mt-0.5 text-blue-400 font-bold text-sm">2.</span>
                                      <p className="text-sm text-blue-200">
                                        Scroll down and tap <strong>Add to Home Screen</strong>
                                      </p>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex items-start gap-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 text-blue-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-sm text-blue-200">
                                      Use your browser's menu to install or add this site to your home screen.
                                    </p>
                                  </div>
                                )}
                              </div>

                              <div className="flex items-start gap-3 rounded-lg bg-amber-500/10 border border-amber-500/20 p-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 text-amber-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-amber-200/80">
                                    <strong>Previously installed?</strong> After uninstalling, your browser may need a
                                    restart before the install option appears again. Close <em>all</em> browser windows,
                                    reopen, and navigate back here.
                                  </p>
                                  <button
                                    onClick={handleRetryInstall}
                                    disabled={retrying}
                                    className="mt-2 inline-flex items-center gap-1.5 rounded bg-amber-500/20 px-3 py-1.5 text-xs font-medium text-amber-300 hover:bg-amber-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 ${retrying ? 'animate-spin' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                    </svg>
                                    {retrying ? 'Refreshing…' : 'Refresh & check again'}
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg border border-borderDefault bg-backgroundInput p-2">
                      <ShieldCheck className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-textPrimary">Trade Approval</h3>
                      <p className="mt-1 text-sm text-textSecondary">
                        Require approval before AI and bot trades execute.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={toggleApproval}
                    disabled={approvalLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${requireApproval ? 'bg-primary' : 'bg-gray-600'} ${approvalLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${requireApproval ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {SETTINGS_SECTIONS.map((section) => (
            <SettingsSection key={section.title} section={section} onNavigate={navigate} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
